import {
  APIReferenceConstructor,
  APIReferenceMember,
  isMathOperationMember,
  memberHasParameters,
  APIReference_apiReference_DatatypeReference_mathOperations as APIDatatypeMathOperation,
  APIReferenceMathOperation,
  ReferenceType,
  MemberType,
} from '@modules/client';
import {
  referenceTypePathMapping,
  referenceTypeToReferencesKey,
  sectionHashByMemberType,
  summaryID,
} from '../constants/apiReferenceConstants';
import { getMemberSlug } from './apiReferenceUtils';
import { getReferences } from '../sharedUI/utils/linkableTypeReferenceUtils';

export const getSummaryTableId = (memberType: MemberType) => {
  return `${summaryID}-${sectionHashByMemberType[memberType]}`;
};

// remove (parent) reference name
// e.g. property 'Bone.TransformCFrame' -> 'TransformCFrame'
//      method 'Actor:BindToMessage' -> 'BindToMessage'
//      constructor 'Parent.new' with parameters => 'new ()'
const memberDividers = ['.', ':'];
const getChildName = (name: string) => {
  let childName = name;
  memberDividers.forEach((divider) => {
    const dividerIdx = name.indexOf(divider);
    if (dividerIdx !== -1) {
      childName = childName.substring(dividerIdx + 1);
    }
  });
  return name.length !== childName.length ? childName : undefined;
};
export const getMemberTitle = (
  name: string,
  parameters?: APIReferenceConstructor['parameters'],
) => {
  const childName = getChildName(name);
  return `${childName ?? name}${parameters ? ' ()' : ''}`;
};

export const getTitlePlainText = (member: APIReferenceMember) =>
  isMathOperationMember(member)
    ? [member.typeA, member.operation, member.typeB].join(' ')
    : getMemberTitle(member.name);

export const getMathOperationSlug = (
  mathOperation: APIDatatypeMathOperation | APIReferenceMathOperation,
) => {
  const hashId = `${mathOperation.typeA}${mathOperation.operation}${mathOperation.typeB}`;
  return hashId;
};

const MAX_SUFFIX_LEN = 10;
export const getUniqueMemberTitleHashId = (
  member: APIReferenceMember,
  needsUniqueSuffix: boolean,
) => {
  const titleHash = getMemberSlug(getTitlePlainText(member));
  if (!needsUniqueSuffix) {
    return titleHash;
  }
  let uniqueSuffix = '';
  if (memberHasParameters(member) && member.parameters) {
    uniqueSuffix = member.parameters.map((p) => p.name).join('-');
    // create short good looking hash id suffix
    if (uniqueSuffix.length > MAX_SUFFIX_LEN) {
      uniqueSuffix = uniqueSuffix.substring(0, MAX_SUFFIX_LEN).replace(/-$/, '');
    }
  }
  const hashId = `${titleHash}${uniqueSuffix ? `-${uniqueSuffix}` : ''}`;
  return hashId;
};

export const getReferencePageUrl = (
  refType: ReferenceType,
  referenceName: string,
  urlHash?: string, // e.g. '#my-property'
) => `/reference/engine/${referenceTypePathMapping[refType]}/${referenceName}${urlHash ?? ''}`;

/**
 * Checks if an API or API member is deprecated using the build-time deprecated map that's
 * built in the scripts/references/index.js script.
 * NOTE (tchu, 2025-10-27): This function is only used to check if the API itself or a Global member is deprecated.
 * It doesn't check if a class member is deprecated.
 * @param refType - The reference type (Class, Enum, Global, etc.)
 * @param referenceName - The API name (e.g., "CylinderMesh", "LuaGlobals")
 * @param memberName - Optional member name (e.g., "print"). If omitted, checks if the API itself is deprecated
 * @returns true if the API or Global member is deprecated
 * @example
 * isEngineApiDeprecated(ReferenceType.CLASS, 'CylinderMesh') // Check if the class is deprecated
 * isEngineApiDeprecated(ReferenceType.GLOBAL, 'LuaGlobals', 'print') // Check if the global function is deprecated
 */
export const isEngineApiDeprecated = (
  refType: ReferenceType,
  referenceName: string,
  memberName?: string,
): boolean => {
  const deprecatedMap = getReferences().DeprecatedMap || {};
  if (memberName) {
    const key = `${referenceTypeToReferencesKey[refType]}.${referenceName}.${memberName}`;
    return deprecatedMap[key] === true;
  }
  const key = `${referenceTypeToReferencesKey[refType]}.${referenceName}`;
  return deprecatedMap[key] === true;
};
